/**
 * Pure math for "collapse on drag" (sc-47423): where an exercise lands, how
 * the grab point is anchored and how fast the edge auto-scroll runs. No DOM
 * access here so it stays unit-testable; the drag controller feeds in rects.
 */

export const DRAG_START_DISTANCE = 4;
export const TOUCH_HOLD_MS = 200;
export const TOUCH_CANCEL_DISTANCE = 10;
export const AUTO_SCROLL_ZONE = 80;
export const AUTO_SCROLL_MAX_SPEED = 720;
export const AUTO_SCROLL_MAX_FRAME_MS = 48;
const GRAB_OFFSET_MIN = 6;
const GRAB_OFFSET_MAX = 26;

export type RowRect = { top: number; height: number };

export type DropSlot = {
  /** Index among the target's rows where the dragged row is inserted. */
  index: number;
  /** Viewport Y of the drop line, relative to the rows' own coordinate space. */
  lineTop: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Pointer distance from the card top, clamped so it lands on a compact row. */
export const clampGrabOffset = (pointerY: number, cardTop: number) =>
  clamp(pointerY - cardTop, GRAB_OFFSET_MIN, GRAB_OFFSET_MAX);

/**
 * Slot = first row whose vertical midpoint is below the pointer; past the
 * last row = end of the day. Line sits halfway in the gap between two rows,
 * 4px above the first, 4px below the last, or at `emptyTop` for an empty day.
 * A `blocked` slot (inside a superset the row can't join) falls to the nearest
 * open one, so what the coach sees is where the row lands.
 */
export const findDropSlot = (
  rows: RowRect[],
  pointerY: number,
  emptyTop: number,
  blocked: ReadonlySet<number> = new Set()
): DropSlot => {
  if (!rows.length) return { index: 0, lineTop: emptyTop };

  const index = rows.findIndex((row) => row.top + row.height / 2 > pointerY);
  const slot = index === -1 ? rows.length : index;
  if (!blocked.has(slot))
    return { index: slot, lineTop: slotLineTop(rows, slot) };

  let best: DropSlot | null = null;
  for (let open = 0; open <= rows.length; open++) {
    if (blocked.has(open)) continue;
    const lineTop = slotLineTop(rows, open);
    if (
      !best ||
      Math.abs(lineTop - pointerY) < Math.abs(best.lineTop - pointerY)
    )
      best = { index: open, lineTop };
  }
  return best as DropSlot;
};

/**
 * Slots inside each block of rows (a superset or complex, as row indexes).
 * The outer edges stay open, since dropping there keeps the block whole.
 */
export const slotsInsideBlocks = (blocks: number[][]) => {
  const blocked = new Set<number>();
  for (const block of blocks)
    for (let slot = Math.min(...block) + 1; slot <= Math.max(...block); slot++)
      blocked.add(slot);
  return blocked;
};

const slotLineTop = (rows: RowRect[], slot: number) => {
  if (slot === 0) return rows[0].top - 4;
  const previous = rows[slot - 1];
  const previousBottom = previous.top + previous.height;
  if (slot === rows.length) return previousBottom + 4;
  return (previousBottom + rows[slot].top) / 2;
};

export type RowKind = "section" | "exercise";

/** Rows [start, end) of a section header and everything under it. */
export type GroupRange = { start: number; end: number };

/**
 * A section group = its header plus every row down to the next header or the
 * end of the day. Exercises above a day's first header belong to no group.
 */
export const getGroupRange = (
  kinds: RowKind[],
  headerIndex: number
): GroupRange => {
  let end = headerIndex + 1;
  while (end < kinds.length && kinds[end] !== "section") end++;
  return { start: headerIndex, end };
};

/**
 * Nearest valid slot for a group drag, or null if the list has none. Only
 * directly above a section header, at the start of the day or at the end of
 * it: anywhere else would split another section. The start of the day is a
 * slot even when exercises come first, which then fall under the moved header.
 * The slots right above and below the dragged group (`own`, same list only)
 * change nothing, so they are skipped.
 */
export const findGroupSlot = (
  rows: RowRect[],
  kinds: RowKind[],
  pointerY: number,
  emptyTop: number,
  own: GroupRange | null
): DropSlot | null => {
  if (!rows.length) return { index: 0, lineTop: emptyTop };

  let best: DropSlot | null = null;
  for (let index = 0; index <= rows.length; index++) {
    if (index > 0 && index < rows.length && kinds[index] !== "section")
      continue;
    if (own && (index === own.start || index === own.end)) continue;
    const lineTop = slotLineTop(rows, index);
    if (
      !best ||
      Math.abs(lineTop - pointerY) < Math.abs(best.lineTop - pointerY)
    )
      best = { index, lineTop };
  }
  return best;
};

/**
 * Dropping an exercise back where it already is changes nothing. Inserting at
 * its own index or the one right after it both leave the order untouched.
 */
export const isSameSlot = (
  sourceIndex: number,
  slotIndex: number,
  sameList: boolean
) => sameList && (slotIndex === sourceIndex || slotIndex === sourceIndex + 1);

/**
 * Auto-scroll velocity (px/s, signed) for one axis. `position` and the
 * container bounds are on the same axis. Quadratic ramp from 0 at the zone's
 * inner edge to max at the container edge; max when the pointer is outside.
 */
export const autoScrollSpeed = (
  position: number,
  start: number,
  end: number,
  zone = AUTO_SCROLL_ZONE,
  maxSpeed = AUTO_SCROLL_MAX_SPEED
) => {
  if (position < start + zone) {
    const t = clamp((start + zone - position) / zone, 0, 1);
    return -maxSpeed * t * t;
  }
  if (position > end - zone) {
    const t = clamp((position - (end - zone)) / zone, 0, 1);
    return maxSpeed * t * t;
  }
  return 0;
};

/** Distance to scroll this frame; frame time is capped so a stall can't leap. */
export const scrollStep = (speed: number, frameMs: number) =>
  (speed * Math.min(frameMs, AUTO_SCROLL_MAX_FRAME_MS)) / 1000;
